import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Card,
  Icon,
  Metric,
  Page,
  PageHeader,
  SearchSelect,
  cardClasses,
  cn,
  linkTextClasses,
  type IconName,
} from '@evogard/ui';
import {
  ASSISTANCE_SERVICES,
  BRAZILIAN_STATE_INFO,
  MANAGEMENT_NOW_HOURS,
  MANAGEMENT_OPEN_STAGES,
  MANAGEMENT_PERIOD_DAYS,
  MANAGEMENT_SCHEDULED_AHEAD_HOURS,
  distanceM,
  tallyByType,
  type AssistanceServiceType,
  type BrazilianState,
  type ManagementOpenStage,
  type ManagementPeriodDays,
  type ManagementStage,
} from '@evogard/domain';
import type { ManagementDispatch } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';
import { can, useSessionContext } from '../../lib/session.js';
import { useRealtimeConnected } from '../../lib/realtime-bus.js';
import { useStoredFlag } from '../../lib/stored-flag.js';
import { useNow } from './ProgressPanel.js';
import './live.js';
import { MANAGEMENT_KEY, SERVICE_ICON, when } from './assistance.js';
import {
  LIVE_INTERVAL_MS,
  LIVE_MAX_MS,
  LiveNotices,
  STAGE_ICON,
  StageMark,
  useLiveFeed,
} from './ManagementLive.js';
import {
  ManagementMap,
  STAGE_COLOR,
  STAGE_LABEL,
  ufOfPoint,
  type MapView,
} from './ManagementMap.js';

// @valor
const PERIOD_LABEL: Record<ManagementPeriodDays, string> = {
  1: 'Hoje',
  7: 'Últimos 7 dias',
  30: 'Últimos 30 dias',
};

/** O que cada etapa da rua quer dizer, numa linha. */
// @valor
const STAGE_HINT: Record<ManagementOpenStage, string> = {
  nao_abriu: 'o prestador não abriu',
  link_aberto: 'ainda não saiu',
  a_caminho: 'indo à origem',
  no_local: 'chegou, conferindo',
  em_servico: 'atendendo',
  em_transito: 'reboque levando',
  no_destino: 'descarregando',
};

type AlertKind = 'signalLost' | 'plateLocked' | 'late';

const SERVICE_LABEL = Object.fromEntries(
  ASSISTANCE_SERVICES.map((s) => [s.type, s.label]),
) as Record<AssistanceServiceType, string>;

/** "há 12 min", "há 2h05", "daqui a 3h10" — o futuro é o agendado. */
function relative(iso: string, now: number): string {
  const diff = Math.round((now - new Date(iso).getTime()) / 60_000);
  const m = Math.abs(diff);
  const text =
    m < 60
      ? `${m} min`
      : m < 1440
        ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`
        : `${Math.round(m / 1440)} d`;
  return diff < 0 ? `daqui a ${text}` : `há ${text}`;
}

/**
 * Assistência 24h › Gestão (`assistencia-35`, 30/10/2026): o desenho do
 * artefato "Gestão da Assistência 24h", para as três perguntas da liderança.
 *
 * 1. **Andamento agora** — quantos serviços estão na rua e em que etapa. Olha
 *    os acionados das últimas 24 h e não depende do período (ver
 *    `assistance-management.ts`, em `@evogard/domain`, sobre a janela).
 * 2. **Onde estão** — o mapa e a fila ao lado, pelo período.
 * 3. **Por tipo** — quantos de cada serviço no período, em quatro fatias.
 *
 * Etapa, alerta, estado e tipo são filtros, e filtram o mapa e a fila juntos.
 * Só leitura: a fila abre o acionamento em Acionamentos, onde cada ato cobra
 * o seu recurso. A tela relê com o mesmo aviso de tempo real que repinta a
 * tabela de Acionamentos (`live.ts`).
 */
export function ManagementScreen() {
  const { data: context } = useSessionContext();
  const tenantId = context?.currentTenant.id;
  const canOpen = can(context, 'assistance.dispatches', 'ver');
  const realtime = useRealtimeConnected();
  const now = useNow();

  /* O ao vivo e as preferências do mapa são de quem olha: ficam no navegador. */
  const [live, setLive] = useStoredFlag('assistencia.gestao.ao-vivo', false);
  const [legendOpen, setLegendOpen] = useStoredFlag('assistencia.gestao.legenda', true);
  const [wide, setWide] = useStoredFlag('assistencia.gestao.largura-total', false);
  const [liveEnded, setLiveEnded] = useState(false);
  const mapArea = useRef<HTMLDivElement>(null);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const sync = () => setFullscreen(document.fullscreenElement === mapArea.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void mapArea.current?.requestFullscreen?.();
  };

  /* O ao vivo desliga sozinho: aba esquecida não consulta a noite inteira. */
  useEffect(() => {
    if (!live) return;
    setLiveEnded(false);
    const t = setTimeout(() => {
      setLive(false);
      setLiveEnded(true);
    }, LIVE_MAX_MS);
    return () => clearTimeout(t);
  }, [live]);

  const [days, setDays] = useState<ManagementPeriodDays>(1);
  const [stage, setStage] = useState<ManagementOpenStage | null>(null);
  const [alert, setAlert] = useState<AlertKind | null>(null);
  const [type, setType] = useState<AssistanceServiceType | null>(null);
  const [uf, setUf] = useState<BrazilianState | null>(null);
  const [view, setView] = useState<MapView>('estados');
  const [selected, setSelected] = useState<number | null>(null);

  const query = useQuery({
    queryKey: [...MANAGEMENT_KEY, tenantId, days],
    queryFn: async () => ok(await api.assistanceManagement.overview({ query: { days } })),
    enabled: tenantId !== undefined,
    /* ao vivo: a cada 5 s; fora dele, o aviso de tempo real (ou um minuto, sem ele) */
    refetchInterval: live ? LIVE_INTERVAL_MS : realtime ? false : 60_000,
  });
  const data = query.data;
  const items = data?.items ?? [];
  const feed = useLiveFeed(data, live, `${tenantId}:${days}`);

  const street = items.filter((d) => d.onStreet);
  const period = items.filter((d) => d.inPeriod);
  const stageCount = (s: ManagementStage) => street.filter((d) => d.stage === s).length;

  /* O mapa e a fila: pela etapa ou pelo alerta, o que está na rua; sem eles, o período. */
  const shown = useMemo(() => {
    let rows: ManagementDispatch[] =
      stage || alert
        ? street.filter((d) => (stage ? d.stage === stage : d[alert!]))
        : period;
    if (type) rows = rows.filter((d) => d.serviceType === type);
    return rows;
  }, [items, stage, alert, type]);
  const inUf = uf ? shown.filter((d) => d.origin && ufOfPoint(d.origin) === uf) : shown;
  const noPlace = shown.filter((d) => d.origin === null).length;
  const selectedItem = items.find((d) => d.id === selected) ?? null;

  const tallies = tallyByType(period);
  const maxTally = Math.max(1, ...tallies.map((t) => t.total));

  const choose = (next: {
    stage?: ManagementOpenStage | null;
    alert?: AlertKind | null;
  }) => {
    setStage(next.stage ?? null);
    setAlert(next.alert ?? null);
    setSelected(null);
  };

  return (
    <Page measure="cheia">
      <PageHeader
        title="Gestão da assistência"
        actions={
          <div className="w-full sm:w-56">
            <SearchSelect
              label="Período"
              options={MANAGEMENT_PERIOD_DAYS.map((d) => ({
                value: String(d),
                label: PERIOD_LABEL[d],
              }))}
              value={String(days)}
              onChange={(v) => v && setDays(Number(v) as ManagementPeriodDays)}
            />
          </div>
        }
      >
        {`Quantos serviços estão na rua e em que etapa, onde estão e quanto de cada tipo saiu. O andamento de agora olha os acionados das últimas ${MANAGEMENT_NOW_HOURS} horas; o mapa, a fila e os tipos olham o período.`}
      </PageHeader>

      {query.isError && (
        <Alert tone="erro" title="Não conseguimos carregar a gestão">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      )}

      <Card
        title="Andamento agora"
        descricao={`Acionados nas últimas ${MANAGEMENT_NOW_HOURS} h e ainda não finalizados. Clique numa etapa para ver só ela no mapa.`}
      >
        {!data ? (
          <div
            className="h-40 animate-pulse rounded-borda bg-superficie-suave"
            aria-busy="true"
          />
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_15rem]">
            <div className="min-w-0">
              <Metric
                className="mb-3"
                label="Na rua agora"
                value={street.length}
                support={
                  street.length === 1
                    ? '1 serviço em andamento'
                    : `${street.length} serviços em andamento`
                }
              />
              <div
                className="flex h-3 gap-0.5 overflow-hidden rounded-full bg-borda"
                aria-hidden="true"
              >
                {MANAGEMENT_OPEN_STAGES.map((s) => {
                  const n = stageCount(s);
                  return n > 0 ? (
                    <i key={s} style={{ flexGrow: n, background: STAGE_COLOR[s] }} />
                  ) : null;
                })}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4 xl:grid-cols-7">
                {MANAGEMENT_OPEN_STAGES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={stage === s}
                    onClick={() => choose({ stage: stage === s ? null : s })}
                    className={cn(
                      'grid gap-0.5 rounded-borda border bg-superficie-suave px-2.5 py-2 text-left hover:border-borda-forte',
                      stage === s ? 'border-texto ring-1 ring-texto' : 'border-borda',
                    )}
                  >
                    <span className="flex items-start justify-between gap-2">
                      <span className="text-xl leading-tight font-extrabold tabular-nums">
                        {stageCount(s)}
                      </span>
                      <StageMark stage={s} />
                    </span>
                    <span className="text-xs font-semibold text-texto-suave">
                      {STAGE_LABEL[s]}
                    </span>
                    <span className="text-[11px] text-texto-tenue">{STAGE_HINT[s]}</span>
                  </button>
                ))}
              </div>
              <div className="mt-3.5 flex flex-wrap gap-2">
                <AlertChip
                  label="sem sinal"
                  n={street.filter((d) => d.signalLost).length}
                  missing={!data.hasNoSignalLimit}
                  pressed={alert === 'signalLost'}
                  onClick={() =>
                    choose({ alert: alert === 'signalLost' ? null : 'signalLost' })
                  }
                />
                <AlertChip
                  label="placa travada"
                  n={street.filter((d) => d.plateLocked).length}
                  missing={false}
                  pressed={alert === 'plateLocked'}
                  onClick={() =>
                    choose({ alert: alert === 'plateLocked' ? null : 'plateLocked' })
                  }
                />
                <AlertChip
                  label="atrasados"
                  n={street.filter((d) => d.late).length}
                  missing={!data.hasArrivalDeadline}
                  pressed={alert === 'late'}
                  onClick={() => choose({ alert: alert === 'late' ? null : 'late' })}
                />
              </div>
            </div>
            <dl className="grid content-start gap-0 border-borda lg:border-l lg:pl-5">
              <Outside
                stage="finalizado"
                label="Finalizados"
                n={period.filter((d) => d.stage === 'finalizado').length}
                help={PERIOD_LABEL[days].toLowerCase()}
              />
              <Outside
                stage="agendado"
                label="Agendados"
                n={items.filter((d) => d.scheduledSoon).length}
                help={`nas próximas ${MANAGEMENT_SCHEDULED_AHEAD_HOURS} h`}
              />
              <Outside
                stage="cancelado"
                label="Cancelados"
                n={period.filter((d) => d.stage === 'cancelado').length}
                help={PERIOD_LABEL[days].toLowerCase()}
              />
            </dl>
          </div>
        )}
      </Card>

      <div
        className={cn(
          'grid items-start gap-5',
          !wide && 'xl:grid-cols-[minmax(0,1fr)_22rem]',
        )}
      >
        <div
          ref={mapArea}
          className={cn('min-w-0', fullscreen && 'overflow-auto bg-fundo p-3')}
        >
          <Card
            bleed
            title="Onde estão os serviços"
            descricao={
              stage || alert
                ? `${shown.length} na rua agora${stage ? ` · ${STAGE_LABEL[stage]}` : ''}${alert ? ` · ${ALERT_LABEL[alert]}` : ''}${type ? ` · ${SERVICE_LABEL[type]}` : ''}`
                : `${shown.length} acionamentos · ${PERIOD_LABEL[days].toLowerCase()}${type ? ` · ${SERVICE_LABEL[type]}` : ''}`
            }
          >
            <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 px-5 pt-4 pb-3.5">
              <nav
                aria-label="Onde no mapa"
                className="flex items-center gap-1.5 text-sm font-semibold"
              >
                {uf ? (
                  <>
                    <button
                      type="button"
                      className={linkTextClasses()}
                      onClick={() => {
                        setUf(null);
                        setSelected(null);
                      }}
                    >
                      Brasil
                    </button>
                    <Icon nome="seta-direita" size={16} className="text-texto-tenue" />
                    <span>{BRAZILIAN_STATE_INFO[uf].name}</span>
                  </>
                ) : (
                  <span>Brasil</span>
                )}
              </nav>
              <button
                type="button"
                aria-pressed={live}
                onClick={() => setLive(!live)}
                title={`Relê a cada ${LIVE_INTERVAL_MS / 1000} s e avisa no mapa o que mudou. Desliga sozinho em ${LIVE_MAX_MS / 60_000} min.`}
                className={cn(
                  'ml-auto inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-semibold',
                  live
                    ? 'border-erro bg-erro-suave text-erro'
                    : 'border-borda text-texto-suave hover:border-borda-forte',
                )}
              >
                <span className="relative grid size-2.5 place-items-center">
                  {live && (
                    <span className="absolute inset-0 rounded-full bg-erro motion-safe:animate-ping" />
                  )}
                  <span
                    className={cn(
                      'size-2.5 rounded-full',
                      live ? 'bg-erro' : 'bg-borda-forte',
                    )}
                  />
                </span>
                {live ? 'Ao vivo' : 'Ativar ao vivo'}
                {live && data && (
                  <span className="font-normal tabular-nums">
                    · {new Date(data.generatedAt).toLocaleTimeString('pt-BR')}
                  </span>
                )}
              </button>
              <div
                role="group"
                aria-label="Visão do mapa"
                className="inline-flex gap-0.5 rounded-borda border border-borda p-0.5"
              >
                {(
                  [
                    ['estados', 'Por estado'],
                    ['servicos', 'Cada serviço'],
                  ] as const
                ).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={view === v}
                    onClick={() => setView(v)}
                    className={cn(
                      'rounded-[7px] px-3 py-1 text-sm font-semibold',
                      view === v ? 'bg-texto text-superficie' : 'text-texto-suave',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="inline-flex gap-1">
                {!fullscreen && (
                  <button
                    type="button"
                    aria-pressed={wide}
                    onClick={() => setWide(!wide)}
                    className="hidden items-center gap-1.5 rounded-borda border border-borda px-2.5 py-1 text-sm font-semibold text-texto-suave hover:border-borda-forte xl:inline-flex"
                  >
                    <Icon nome="mover" size={16} />
                    {wide ? 'Mapa ao lado da lista' : 'Largura total'}
                  </button>
                )}
                <button
                  type="button"
                  aria-pressed={fullscreen}
                  onClick={toggleFullscreen}
                  className="inline-flex items-center gap-1.5 rounded-borda border border-borda px-2.5 py-1 text-sm font-semibold text-texto-suave hover:border-borda-forte"
                >
                  <Icon nome="expandir" size={16} />
                  {fullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                </button>
              </div>
            </div>
            {liveEnded && !live && (
              <p className="mx-5 mb-3 rounded-borda bg-superficie-suave px-3 py-2 text-xs text-texto-suave">
                O ao vivo desligou sozinho depois de {LIVE_MAX_MS / 60_000} minutos. Ative
                de novo se ainda estiver acompanhando.
              </p>
            )}
            {!data ? (
              <div
                className="aspect-square w-full animate-pulse bg-superficie-suave"
                aria-busy="true"
              />
            ) : (
              <ManagementMap
                items={shown}
                view={view}
                uf={uf}
                onUf={(next) => {
                  setUf(next);
                  setSelected(null);
                }}
                selected={selected}
                onSelect={setSelected}
                fresh={feed.fresh}
                height={fullscreen ? 'calc(100vh - 9.5rem)' : wide ? '46rem' : undefined}
                detail={
                  selectedItem && (
                    <DispatchCard
                      d={selectedItem}
                      now={now}
                      canOpen={canOpen}
                      onClose={() => setSelected(null)}
                    />
                  )
                }
              >
                <div
                  className={cn(
                    'pointer-events-none absolute bottom-3 left-3 z-10 flex flex-col items-start',
                    /* com aviso no canto direito, a legenda abre ao lado dele, e não por baixo */
                    feed.notices.length > 0
                      ? 'max-w-[max(12rem,calc(100%-23rem))]'
                      : 'max-w-[calc(100%-1.5rem)]',
                  )}
                >
                  <Legend
                    pins={view === 'servicos' || uf !== null}
                    open={legendOpen}
                    onToggle={() => setLegendOpen(!legendOpen)}
                  />
                </div>
                {feed.notices.length > 0 && (
                  <div className="absolute right-3 bottom-3 z-30 w-[min(20rem,calc(100%-1.5rem))]">
                    <LiveNotices
                      notices={feed.notices}
                      onPick={setSelected}
                      onDismiss={feed.dismiss}
                    />
                  </div>
                )}
              </ManagementMap>
            )}
            {noPlace > 0 && (
              <p className="px-5 pb-4 text-xs text-texto-tenue">
                {noPlace === 1
                  ? '1 acionamento não tem coordenada no endereço e não aparece no mapa; ele está na lista.'
                  : `${noPlace} acionamentos não têm coordenada no endereço e não aparecem no mapa; eles estão na lista.`}
              </p>
            )}
          </Card>
        </div>

        <Queue
          rows={inUf}
          now={now}
          selected={selected}
          onSelect={setSelected}
          canOpen={canOpen}
          loading={!data}
        />
      </div>

      <Card
        title="Acionamentos por tipo"
        descricao={`${period.length} ${PERIOD_LABEL[days].toLowerCase()}, sem rascunhos. Clique num tipo para filtrar o mapa e a lista.`}
      >
        {!data ? (
          <div
            className="h-48 animate-pulse rounded-borda bg-superficie-suave"
            aria-busy="true"
          />
        ) : tallies.length === 0 ? (
          <p className="py-8 text-center text-sm text-texto-suave">
            Nenhum acionamento {PERIOD_LABEL[days].toLowerCase()}.
          </p>
        ) : (
          <>
            <div className="grid gap-2.5">
              {tallies.map((t) => {
                const on = type === t.type;
                const share = Math.round((t.total / period.length) * 100);
                const parts = [
                  ['finalizado', t.finished, 'Finalizados'],
                  ['em_transito', t.inProgress, 'Em andamento'],
                  ['agendado', t.scheduled, 'Agendados'],
                  ['cancelado', t.canceled, 'Cancelados'],
                ] as const;
                return (
                  <button
                    key={t.type}
                    type="button"
                    aria-pressed={on}
                    onClick={() => {
                      setType(on ? null : t.type);
                      setSelected(null);
                    }}
                    className="grid grid-cols-[8.5rem_minmax(0,1fr)_4.5rem] items-center gap-3.5 rounded-borda py-1 text-left sm:grid-cols-[11rem_minmax(0,1fr)_5.5rem]"
                  >
                    <span
                      className={cn(
                        'flex items-center gap-2 text-sm font-semibold',
                        on && 'underline underline-offset-4',
                      )}
                    >
                      <Icon
                        nome={SERVICE_ICON[t.type]}
                        size={16}
                        className="text-texto-suave"
                      />
                      {SERVICE_LABEL[t.type]}
                    </span>
                    <span className="flex h-5 overflow-hidden rounded bg-superficie-suave">
                      {parts.map(([s, n, label]) =>
                        n > 0 ? (
                          <i
                            key={s}
                            title={`${label}: ${n}`}
                            style={{
                              width: `${(n / maxTally) * 100}%`,
                              background: STAGE_COLOR[s],
                              opacity: s === 'cancelado' ? 0.6 : 1,
                            }}
                          />
                        ) : null,
                      )}
                    </span>
                    <span className="text-right">
                      <b className="block text-lg leading-tight tabular-nums">
                        {t.total}
                      </b>
                      <span className="text-xs text-texto-tenue">{share}%</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap gap-3.5 text-xs text-texto-suave">
              {(
                [
                  ['finalizado', 'Finalizado'],
                  ['em_transito', 'Em andamento'],
                  ['agendado', 'Agendado'],
                  ['cancelado', 'Cancelado'],
                ] as const
              ).map(([s, label]) => (
                <span key={s} className="inline-flex items-center gap-1.5">
                  <i
                    className="h-2 w-3 rounded-sm"
                    style={{
                      background: STAGE_COLOR[s],
                      opacity: s === 'cancelado' ? 0.6 : 1,
                    }}
                  />
                  {label}
                </span>
              ))}
            </div>
          </>
        )}
      </Card>
    </Page>
  );
}

// @valor
const ALERT_LABEL: Record<AlertKind, string> = {
  signalLost: 'sem sinal',
  plateLocked: 'placa travada',
  late: 'atrasados',
};

/** O alerta é filtro; sem o prazo da base, ele diz que não conta, em vez de mostrar zero. */
function AlertChip({
  label,
  n,
  missing,
  pressed,
  onClick,
}: {
  label: string;
  n: number;
  missing: boolean;
  pressed: boolean;
  onClick: () => void;
}) {
  if (missing)
    return (
      <span className="inline-flex items-center rounded-full border border-borda px-3 py-1 text-xs text-texto-tenue">
        {label}: sem prazo configurado
      </span>
    );
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      disabled={n === 0 && !pressed}
      className="disabled:cursor-default"
    >
      <Badge
        tone={n > 0 ? 'erro' : 'neutro'}
        className={cn(pressed && 'ring-1 ring-current')}
      >
        {n} {label}
      </Badge>
    </button>
  );
}

/**
 * O card do serviço escolhido no mapa ou na fila: quem, o quê, onde e quando,
 * em blocos — e o caminho para o acionamento, onde cada ato cobra o recurso
 * dele. A distância é em linha reta: a do percurso é a da cotação, e mora na
 * mesa do acionamento.
 */
function DispatchCard({
  d,
  now,
  canOpen,
  onClose,
}: {
  d: ManagementDispatch;
  now: number;
  canOpen: boolean;
  onClose: () => void;
}) {
  const flags = [
    d.signalLost && 'Sem sinal do prestador',
    d.plateLocked && 'Placa travada: espera quem acionou destravar',
    d.late && 'Atrasado para chegar',
  ].filter((f): f is string => !!f);
  const km =
    d.origin && d.destination
      ? (distanceM(d.origin, d.destination) / 1000).toLocaleString('pt-BR', {
          maximumFractionDigits: 1,
        })
      : null;
  const moments = [
    d.status === 'agendado' && d.scheduledFor
      ? ['Marcado para', `${when(d.scheduledFor)} · ${relative(d.scheduledFor, now)}`]
      : null,
    d.executedAt
      ? ['Acionado', `${when(d.executedAt)} · ${relative(d.executedAt, now)}`]
      : null,
    d.canceledAt
      ? ['Cancelado', `${when(d.canceledAt)} · ${relative(d.canceledAt, now)}`]
      : null,
    d.stage !== 'agendado' && d.stage !== 'cancelado'
      ? [`${STAGE_LABEL[d.stage]} desde`, `${when(d.since)} · ${relative(d.since, now)}`]
      : null,
  ].filter((m): m is [string, string] => m !== null);

  return (
    <section
      aria-label={`Acionamento ${d.code}`}
      className={cn(cardClasses({ padding: false }), 'overflow-hidden shadow-lg')}
    >
      <header className="flex items-start gap-3 border-b border-borda p-3.5">
        <span
          aria-hidden="true"
          className="grid size-10 shrink-0 place-items-center rounded-lg"
          style={{
            color: STAGE_COLOR[d.stage],
            background: `color-mix(in oklch, ${STAGE_COLOR[d.stage]} 14%, transparent)`,
          }}
        >
          <Icon nome={SERVICE_ICON[d.serviceType]} size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs text-texto-tenue">{d.code}</p>
          <h3 className="text-base leading-tight font-semibold">
            {SERVICE_LABEL[d.serviceType]}
          </h3>
          <span
            className="mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11.5px] font-bold"
            style={{
              color: STAGE_COLOR[d.stage],
              background: `color-mix(in oklch, ${STAGE_COLOR[d.stage]} 13%, transparent)`,
            }}
          >
            <Icon nome={STAGE_ICON[d.stage]} size={16} />
            {STAGE_LABEL[d.stage]}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar o card"
          className="-mt-1 -mr-1 grid size-8 place-items-center rounded-lg text-texto-suave hover:bg-superficie-suave"
        >
          <Icon nome="fechar" size={16} />
        </button>
      </header>

      {flags.length > 0 && (
        <ul className="flex flex-col gap-1 border-b border-borda bg-erro-suave px-3.5 py-2.5 text-xs font-semibold text-erro">
          {flags.map((f) => (
            <li key={f} className="flex items-center gap-1.5">
              <Icon nome="atencao" size={16} />
              {f}
            </li>
          ))}
        </ul>
      )}

      <div className="flex max-h-[min(30rem,55vh)] flex-col gap-3 overflow-y-auto p-3.5 text-sm">
        <CardBlock icon="veiculo" title="Veículo e associado">
          <CardFact label="Placa" value={d.plate} mono />
          <CardFact label="Associado" value={d.memberName} />
        </CardBlock>
        <CardBlock icon="mapa" title="Trajeto">
          <CardFact label="Origem" value={d.originLabel} />
          {d.destination && <CardFact label="Destino" value={d.destinationLabel} />}
          {km && <CardFact label="Em linha reta" value={`${km} km`} />}
        </CardBlock>
        <CardBlock icon="pessoa" title="Prestador">
          <CardFact label="Empresa" value={d.providerName} />
        </CardBlock>
        <CardBlock icon="relogio" title="Quando">
          {moments.map(([label, value]) => (
            <CardFact key={label} label={label} value={value} />
          ))}
        </CardBlock>
      </div>

      {canOpen && (
        <footer className="border-t border-borda px-3.5 py-2.5">
          <Link
            to="/assistance/dispatches"
            search={{ open: d.id }}
            className={cn(linkTextClasses(), 'inline-flex items-center gap-1.5 text-sm')}
          >
            Abrir o acionamento
            <Icon nome="seta-direita" size={16} />
          </Link>
        </footer>
      )}
    </section>
  );
}

function CardBlock({
  icon,
  title,
  children,
}: {
  icon: IconName;
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="rotulo mb-1 flex items-center gap-1.5">
        <Icon nome={icon} size={16} />
        {title}
      </p>
      <dl className="grid grid-cols-[6.5rem_minmax(0,1fr)] gap-x-2 gap-y-0.5">
        {children}
      </dl>
    </div>
  );
}

/** Um fato do card. O vazio diz que não há, e não some — "—" seria um dado inventado. */
function CardFact({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  return (
    <>
      <dt className="text-texto-tenue">{label}</dt>
      <dd
        className={cn(
          value ? 'text-texto' : 'text-texto-tenue italic',
          mono && 'font-mono',
        )}
      >
        {value ?? 'não informado'}
      </dd>
    </>
  );
}

function Outside({
  stage,
  label,
  n,
  help,
}: {
  stage: ManagementStage;
  label: string;
  n: number;
  help: string;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-2 border-b border-borda py-2 last:border-b-0">
      <dt className="flex items-center gap-2 text-sm font-semibold text-texto-suave">
        <StageMark stage={stage} size="sm" />
        {label}
      </dt>
      <dd className="text-lg font-bold tabular-nums">{n}</dd>
      <dd className="col-span-2 -mt-1 pl-8 text-xs text-texto-tenue">{help}</dd>
    </div>
  );
}

/**
 * A legenda flutua no canto do mapa e recolhe num botão — com o mapa em tela
 * cheia ela não pode empurrar nada para baixo. Recolhida ou aberta é
 * preferência de quem olha, e fica no navegador.
 */
function Legend({
  pins,
  open,
  onToggle,
}: {
  pins: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={cn(
        cardClasses({ padding: false }),
        'pointer-events-auto max-w-[34rem] text-xs text-texto-suave shadow-lg',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 px-3 py-2 font-semibold text-texto"
      >
        <Icon nome="informacao" size={16} />
        Legenda
        <Icon nome={open ? 'seta-baixo' : 'seta-cima'} size={16} className="ml-auto" />
      </button>
      {open && (
        <div className="flex max-h-[40vh] flex-col gap-2 overflow-y-auto border-t border-borda px-3 py-2.5">
          {!pins ? (
            <p>
              A cor do estado e o tamanho da bolha são a quantidade de acionamentos.
              Clique num estado para ver cada serviço.
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <b className="text-texto">Cor do pino: etapa</b>
                {[
                  ...MANAGEMENT_OPEN_STAGES,
                  'finalizado' as const,
                  'agendado' as const,
                  'cancelado' as const,
                ].map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5">
                    <i
                      className="size-2 rounded-full"
                      style={{ background: STAGE_COLOR[s] }}
                    />
                    {STAGE_LABEL[s]}
                  </span>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1">
                <b className="text-texto">Ícone: tipo de serviço</b>
                {ASSISTANCE_SERVICES.map((s) => (
                  <span
                    key={s.type}
                    className="inline-flex items-center gap-1.5 text-texto"
                  >
                    <Icon nome={SERVICE_ICON[s.type]} size={16} />
                    {s.label}
                  </span>
                ))}
              </div>
              <span className="inline-flex items-center gap-1.5">
                <svg
                  width="58"
                  height="16"
                  aria-hidden="true"
                  className="overflow-visible"
                >
                  <ellipse
                    cx="29"
                    cy="8"
                    rx="28"
                    ry="7.5"
                    className="fill-texto-suave/10 stroke-texto-suave"
                    strokeDasharray="2 3"
                    strokeWidth="1"
                  />
                  <circle cx="12" cy="8" r="3" className="fill-texto-suave" />
                  <path
                    d="M15 8H42"
                    className="stroke-texto-suave"
                    strokeDasharray="3 3"
                    strokeWidth="1.5"
                  />
                  <rect
                    x="42"
                    y="5"
                    width="6"
                    height="6"
                    rx="1.5"
                    className="fill-superficie stroke-texto-suave"
                    strokeWidth="1.5"
                  />
                </svg>
                reboque: o contorno junta a origem e o destino do mesmo serviço
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="relative grid size-3 place-items-center">
                  <span className="absolute inset-0 rounded-full bg-texto-suave/40 motion-safe:animate-ping" />
                  <span className="size-2 rounded-full bg-texto-suave" />
                </span>
                no ao vivo, o pino pulsa quando o serviço entra ou muda de etapa
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** A fila ao lado do mapa: o que precisa de atenção primeiro, depois o que está na rua. */
function Queue({
  rows,
  now,
  selected,
  onSelect,
  canOpen,
  loading,
}: {
  rows: ManagementDispatch[];
  now: number;
  selected: number | null;
  onSelect: (id: number | null) => void;
  canOpen: boolean;
  loading: boolean;
}) {
  const flags = (d: ManagementDispatch) =>
    Number(d.signalLost) + Number(d.plateLocked) + Number(d.late);
  const sorted = [...rows].sort(
    (a, b) =>
      flags(b) - flags(a) ||
      Number(b.onStreet) - Number(a.onStreet) ||
      b.since.localeCompare(a.since),
  );
  const shown = sorted.slice(0, 60);
  return (
    <Card
      bleed
      title="Acionamentos"
      descricao={
        loading
          ? undefined
          : rows.length > 60
            ? `Os 60 primeiros de ${rows.length}`
            : `${rows.length} na lista`
      }
    >
      {loading ? (
        <div
          className="mx-5 mb-5 h-96 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : rows.length === 0 ? (
        <p className="px-5 pb-6 text-sm text-texto-tenue">
          Nenhum acionamento com esses filtros.
        </p>
      ) : (
        <ul className="max-h-[48rem] overflow-y-auto border-t border-borda">
          {shown.map((d) => {
            const flag = d.signalLost
              ? 'sem sinal'
              : d.plateLocked
                ? 'placa travada'
                : d.late
                  ? 'atrasado'
                  : null;
            return (
              <li
                key={d.id}
                className={cn(
                  'border-b border-borda',
                  d.id === selected && 'bg-superficie-suave',
                )}
              >
                <button
                  type="button"
                  onClick={() => onSelect(d.id === selected ? null : d.id)}
                  className="grid w-full grid-cols-[1fr_auto] gap-x-2.5 gap-y-0.5 px-5 py-2.5 text-left hover:bg-superficie-suave"
                >
                  <span className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-texto-tenue">{d.code}</span>
                    <Icon
                      nome={SERVICE_ICON[d.serviceType]}
                      size={16}
                      className="text-texto-suave"
                    />
                    <b className="text-sm">{SERVICE_LABEL[d.serviceType]}</b>
                  </span>
                  <span
                    className="self-start rounded-full px-2 py-0.5 text-[11.5px] font-bold whitespace-nowrap"
                    style={{
                      color: STAGE_COLOR[d.stage],
                      background: `color-mix(in oklch, ${STAGE_COLOR[d.stage]} 13%, transparent)`,
                    }}
                  >
                    {STAGE_LABEL[d.stage]}
                  </span>
                  <span className="col-span-2 text-xs text-texto-suave">
                    {[
                      d.plate,
                      d.providerName,
                      d.stage === 'agendado' && d.scheduledFor
                        ? `marcado ${relative(d.scheduledFor, now)}`
                        : relative(d.since, now),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {flag && <b className="ml-1.5 text-erro">{flag}</b>}
                  </span>
                </button>
                {d.id === selected && canOpen && (
                  <div className="px-5 pb-2.5">
                    <Link
                      to="/assistance/dispatches"
                      search={{ open: d.id }}
                      className={linkTextClasses()}
                    >
                      Abrir o acionamento
                    </Link>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
